import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Play, ArrowLeft, LogOut, BookOpen, Download, FileText, Video, File as FileIcon, Settings, Upload, Trash2, Clock, Calendar } from "lucide-react";
import { useAuth, API_BASE, formatApiError } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";
import { ICH_LOGO } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KIND_ICONS = { pdf: FileText, video: Video, file: FileIcon };
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const downloadResource = (r) => {
  window.open(`${API_BASE}/resources/${r.id}/download`, "_blank");
};

const downloadPrep = (r) => {
  window.open(`${API_BASE}/prep-uploads/${r.id}/download`, "_blank");
};

const PrepUploadsPanel = ({ courseId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/client/prep-uploads?course_id=${encodeURIComponent(courseId)}`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load your uploads.");
      }
    } finally { setLoading(false); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("course_id", courseId);
    fd.append("title", title);
    fd.append("description", description);
    try {
      await axios.post(`${API_BASE}/client/prep-uploads`, fd, { withCredentials: true });
      toast.success("Uploaded. Devontae will review before your call.");
      setTitle(""); setDescription(""); setFile(null);
      const input = document.getElementById(`prep-input-${courseId}`);
      if (input) input.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this upload?")) return;
    try {
      await axios.delete(`${API_BASE}/client/prep-uploads/${id}`, { withCredentials: true });
      setItems((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  return (
    <div data-testid={`prep-panel-${courseId}`}>
      <div className="flex items-start gap-3 p-4 mb-6 border border-[#d9b87d]/40 bg-[#d9b87d]/5">
        <Clock size={16} strokeWidth={1.5} className="text-[#d9b87d] shrink-0 mt-0.5" />
        <div className="text-[rgba(232,226,212,0.85)] text-sm leading-relaxed">
          <strong className="text-[#d9b87d] font-label">Deadline:</strong> upload your materials at least{" "}
          <strong className="text-[#efe7d2]">24 hours before your scheduled call</strong> so Devontae has time to review them.
        </div>
      </div>

      <form onSubmit={upload} className="space-y-4 mb-8" data-testid={`prep-upload-form-${courseId}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            data-testid={`prep-title-${courseId}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (e.g. Deal memo, Q3 P&L)"
            className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2]"
          />
          <input
            data-testid={`prep-desc-${courseId}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Notes / questions (optional)"
            className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <input
            id={`prep-input-${courseId}`}
            data-testid={`prep-file-${courseId}`}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-[rgba(232,226,212,0.7)] text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-[rgba(232,226,212,0.25)] file:bg-transparent file:text-[#efe7d2] file:font-label file:text-[0.62rem] file:cursor-pointer cursor-pointer"
          />
          <button data-testid={`prep-submit-${courseId}`} type="submit" disabled={uploading || !file} className="btn-solid disabled:opacity-60">
            {uploading ? "Uploading..." : "Upload for review"} <Upload size={14} />
          </button>
        </div>
      </form>

      <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Your uploads ({items.length})</div>
      {loading ? (
        <div className="text-[rgba(232,226,212,0.5)] text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-[rgba(232,226,212,0.55)] text-sm italic" data-testid={`prep-empty-${courseId}`}>
          Nothing uploaded yet. Share deal memos, financials, screenshots, or any context you want reviewed.
        </div>
      ) : (
        <ul className="space-y-3" data-testid={`prep-list-${courseId}`}>
          {items.map((r) => {
            const Icon = KIND_ICONS[r.kind] || FileIcon;
            return (
              <li key={r.id} className="flex items-center gap-4 border-b border-[rgba(232,226,212,0.08)] pb-3">
                <Icon size={18} strokeWidth={1.5} className="text-[#d9b87d] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[#efe7d2] text-lg truncate">{r.title}</div>
                  <div className="font-label text-[rgba(232,226,212,0.5)] text-[0.62rem] mt-1">
                    {r.filename} · {fmtSize(r.size)} · {r.content_type}
                  </div>
                  {r.description && <div className="text-[rgba(232,226,212,0.65)] text-sm mt-1">{r.description}</div>}
                </div>
                <button data-testid={`prep-download-${r.id}`} onClick={() => downloadPrep(r)} className="nav-link inline-flex items-center gap-2">
                  <Download size={12} /> View
                </button>
                <button data-testid={`prep-delete-${r.id}`} onClick={() => del(r.id)} className="nav-link inline-flex items-center gap-2 hover:text-[#ff6b6b]">
                  <Trash2 size={12} /> Delete
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ResourceList = ({ courseId }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/consultations/${courseId}/resources`, { withCredentials: true });
        setResources(data);
      } catch (err) {
        if (err.response?.status !== 403) {
          toast.error(formatApiError(err.response?.data?.detail) || "Could not load resources.");
        }
      } finally { setLoading(false); }
    })();
  }, [courseId]);

  if (loading) return <div className="text-[rgba(232,226,212,0.5)] text-sm">Loading notes...</div>;
  if (resources.length === 0) {
    return (
      <div className="text-[rgba(232,226,212,0.55)] text-sm italic" data-testid={`resources-empty-${courseId}`}>
        No notes have been posted yet. Devontae will upload your notes, commentary, and analysis after your call.
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid={`resources-list-${courseId}`}>
      {resources.map((r) => {
        const Icon = KIND_ICONS[r.kind] || FileIcon;
        return (
          <li key={r.id} className="flex items-center gap-4 border-b border-[rgba(232,226,212,0.08)] pb-3">
            <Icon size={18} strokeWidth={1.5} className="text-[#d9b87d] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-[#efe7d2] text-lg truncate">{r.title}</div>
              <div className="font-label text-[rgba(232,226,212,0.5)] text-[0.62rem] mt-1">
                {fmtSize(r.size)} · {r.content_type}
              </div>
              {r.description && <div className="text-[rgba(232,226,212,0.65)] text-sm mt-1">{r.description}</div>}
            </div>
            <button data-testid={`resource-download-${r.id}`} onClick={() => downloadResource(r)} className="btn-outline">
              <Download size={12} /> Download
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const LessonPlayer = ({ course, onClose, schedulingUrl }) => (
  <div data-testid={HOME.lessonPlayer} className="fixed inset-0 z-[100] bg-[#0a1628]/95 backdrop-blur-md overflow-y-auto p-6 fade-up" style={{ animationDuration: "300ms" }}>
    <div className="max-w-5xl mx-auto glass p-8 sm:p-12 relative my-8">
      <button data-testid={HOME.lessonClose} onClick={onClose} className="absolute top-4 right-6 nav-link">Close ✕</button>
      <div className="font-label text-[rgba(232,226,212,0.55)] mb-3">Client Portal</div>
      <h2 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">{course.title}</h2>
      <p className="mt-4 text-[rgba(232,226,212,0.7)] max-w-2xl">{course.summary}</p>

      {schedulingUrl && (
        <div className="mt-8 p-5 border border-[#d9b87d]/40 bg-[#d9b87d]/5 flex flex-wrap items-center justify-between gap-4" data-testid="portal-scheduling-card">
          <div>
            <div className="font-label text-[#d9b87d] mb-1 flex items-center gap-2"><Calendar size={12} /> Schedule your call</div>
            <div className="text-[rgba(232,226,212,0.7)] text-sm">Haven&apos;t picked a time yet? Book it now.</div>
          </div>
          <a href={schedulingUrl} target="_blank" rel="noopener noreferrer" data-testid="portal-schedule-link" className="btn-solid inline-flex">
            Book Time Slot <ArrowUpRight size={14} />
          </a>
        </div>
      )}

      <div className="mt-10 border-t border-[rgba(232,226,212,0.12)] pt-8">
        <div className="flex items-center gap-3 mb-6">
          <Upload size={14} strokeWidth={1.5} className="text-[#d9b87d]" />
          <h3 className="font-display text-[#efe7d2] text-2xl">Pre-call materials</h3>
        </div>
        <p className="text-[rgba(232,226,212,0.65)] text-sm mb-6 max-w-2xl">
          Upload anything you&apos;d like Devontae to review before your consultation — deals, docs, financials, screenshots, questions, context.
        </p>
        <PrepUploadsPanel courseId={course.id} />
      </div>

      <div className="mt-12 border-t border-[rgba(232,226,212,0.12)] pt-8">
        <div className="flex items-center gap-3 mb-6">
          <FileText size={14} strokeWidth={1.5} className="text-[#d9b87d]" />
          <h3 className="font-display text-[#efe7d2] text-2xl">Post-call notes, commentary &amp; analysis</h3>
        </div>
        <p className="text-[rgba(232,226,212,0.65)] text-sm mb-6 max-w-2xl">
          After your call, Devontae will post detailed notes, commentary, analysis, and recommendations here — private to you.
        </p>
        <ResourceList courseId={course.id} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [schedulingUrl, setSchedulingUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/config`);
        setSchedulingUrl(data.scheduling_url || null);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/auth/my-consultations`, { withCredentials: true });
        setCourses(data);
      } catch (_e) {
        toast.error("Could not load your consultations.");
      } finally {
        setLoading(false);
      }
    })();
  }, [checked, user, navigate]);

  if (!checked || loading) return (
    <div className="min-h-screen flex items-center justify-center text-[rgba(232,226,212,0.6)] font-label">Loading...</div>
  );

  return (
    <div data-testid={HOME.dashboardRoot} className="min-h-screen px-6 sm:px-10 py-12">
      <div className="grain" />
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between mb-16">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center overflow-hidden" style={{ border: "1px solid rgba(232,226,212,0.25)" }}>
            <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
          </div>
          <div className="font-label text-[#efe7d2]">Innovative Capital Holdings</div>
        </Link>
        <div className="flex items-center gap-6">
          {user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="nav-link inline-flex items-center gap-2"><Settings size={12} /> Admin</Link>
          )}
          <Link to="/" className="nav-link inline-flex items-center gap-2"><ArrowLeft size={12} /> Back to site</Link>
          <button data-testid={HOME.navLogout} onClick={async () => { await logout(); navigate("/"); }} className="nav-link inline-flex items-center gap-2"><LogOut size={12} /> Logout</button>
        </div>
      </nav>

      <header className="max-w-[1400px] mx-auto mb-16">
        <div className="font-label text-[rgba(232,226,212,0.55)] mb-4 flex items-center gap-3">
          <BookOpen size={12} strokeWidth={1.5} /> Client Portal
        </div>
        <h1 className="font-display text-[#efe7d2] text-5xl sm:text-6xl leading-[1.05]">
          Welcome, <span className="font-italic-display">{user?.name?.split(" ")[0] || "Client"}.</span>
        </h1>
        <p className="mt-6 text-[rgba(232,226,212,0.7)] max-w-2xl leading-relaxed">
          Your consultations are listed below. Open one to upload pre-call materials or download the notes, commentary, and analysis Devontae posts after your call.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto">
        {courses.length === 0 ? (
          <div className="glass p-12 text-center">
            <div className="font-display text-[#efe7d2] text-3xl">No consultations yet.</div>
            <p className="mt-4 text-[rgba(232,226,212,0.65)] max-w-md mx-auto">Book a 30-minute strategy consultation to get started. Your call and materials will appear here.</p>
            <Link to="/#consulting" className="btn-solid mt-8 inline-flex">Book a Consultation <ArrowUpRight size={14} /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(232,226,212,0.1)] border border-[rgba(232,226,212,0.1)]">
            {courses.map((c) => (
              <div key={c.id} data-testid={HOME.dashboardCourseCard(c.id)} className="bg-[#0a1628] p-10 group flex flex-col">
                <div className="font-label text-[rgba(232,226,212,0.45)]">Booked</div>
                <h3 className="font-display text-[#efe7d2] text-2xl sm:text-3xl leading-[1.15] mt-4">{c.title}</h3>
                <p className="mt-5 text-[rgba(232,226,212,0.65)] text-sm leading-[1.75] flex-1">{c.summary}</p>
                <div className="mt-6 font-label text-[rgba(232,226,212,0.5)] text-[0.62rem]">Upload materials 24h before call · Notes delivered after</div>
                <button data-testid={HOME.dashboardOpenLesson(c.id)} onClick={() => setActive(c)} className="btn-solid mt-8 self-start">
                  Open Consultation <Play size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {active && <LessonPlayer course={active} onClose={() => setActive(null)} schedulingUrl={schedulingUrl} />}
    </div>
  );
};

export default Dashboard;
